import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import { Link } from "react-router-dom";
import { CalendarIcon, MapPinIcon } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

export interface EventCardProps {
    id: string;
    title: string;
    date: string;
    image: string;
    location: string;
    className?: string;
    href?: string;
    variant?: "default" | "outline";
    bgColor?: "default" | "secondary";
    description?: string;
}

const variants = cva(
    "rounded-xl overflow-hidden flex flex-col h-full",
    {
        variants: {
            variant: {
                default: "shadow-lg  border border-gray-100 hover:shadow-xl transition-shadow duration-200",
                outline: "border border-gray-200",
            },
            bgColor: {
                default: "bg-white",
                secondary: "bg-gray-50",
            },
        },
        defaultVariants: {
            variant: "default",
            bgColor: "default",
        },
    }
);

export function EventCard({
    id,
    title,
    date,
    image,
    location,
    className,
    href,
    variant,
    bgColor,
    description,
}: EventCardProps) {
    const content = (
        <div
            className={cn(
                variants({ variant: variant, bgColor: bgColor }),
                "group w-full",
                className
            )}
        >
            {/* Image en haut avec ratio 16:9 */}
            <Dialog>
                <DialogTrigger asChild>
                    <div
                        className={cn(
                            "w-full aspect-video relative bg-gray-100 cursor-pointer overflow-hidden group/img",
                            { "bg-gray-200": !href }
                        )}
                        onMouseEnter={() => href && (document.body.style.cursor = "pointer")}
                        onMouseLeave={() => (document.body.style.cursor = "auto")}
                    >
                        <div className="relative w-full h-full transform transition-transform duration-500 group-hover/img:scale-110">
                            <img
                                src={image}
                                alt={title}
                                className="object-cover transition-all duration-500 group-hover/img:brightness-90"
                                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                            />
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover/img:opacity-100 transition-opacity duration-300"></div>
                        </div>
                    </div>
                </DialogTrigger>
                <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-white/10 border-none flex items-center justify-center">
                    <div className="relative w-[80vw] h-[80vh]">
                        <img
                            src={image}
                            alt={title}
                            className="object-contain"
                            style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                        />
                    </div>
                </DialogContent>
            </Dialog>

            {/* Contenu en dessous de l'image */}
            <div className="p-4 flex flex-col flex-grow">
                <div className="flex flex-col md:flex-row md:items-center mb-2 gap-4">
                    {/* Date */}
                    <p className="text-sm font-medium text-gray-500 flex items-center">
                        <CalendarIcon className="w-4 h-4 mr-1 flex-shrink-0" /> {date}
                    </p>

                    {/* Localisation */}
                    {location && (
                        <div className="flex items-center text-gray-600 text-sm">
                            <MapPinIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                            <span className="truncate">{location}</span>
                        </div>
                    )}

                </div>

                {/* Titre */}
                <h2 className="text-lg md:text-xl font-bold text-gray-800 leading-snug line-clamp-2 mb-2">
                    {title}
                </h2>


                {/* Description */}
                {description && (
                    <p className="mt-2 text-gray-600 text-sm whitespace-pre-line flex-grow">
                        {description}
                    </p>
                )}
            </div>
        </div>
    );

    return href ? (
        <Link to={href} className="block">
            {content}
        </Link>
    ) : (
        content
    );
}

export default EventCard;
